import {AsyncPipe} from '@angular/common';
import {ChangeDetectorRef, Component, HostListener, OnInit, QueryList, ViewChildren,} from '@angular/core';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatTooltip} from '@angular/material/tooltip';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';

import {Asset, AssetService, AssetType} from '@app/asset';
import {InspectorService} from '@app/inspector/inspector.service';
import {PreferencesService} from '@app/preferences/preferences.service';
import {ColorSwatchMap} from '@app/preferences/swatch-picker/swatch-picker.component';
import {ManagedComponent} from '@app/shared/managed.component';
import {StateService} from '@app/state/state.service';
import {IViewable, ViewerTool} from '@app/viewer/viewable.interface';
import {ViewerToolsService} from '@app/viewer/viewer-tools.service';
import {combineLatest, Observable} from 'rxjs';

import {delay, filter, map, mergeMap, tap} from 'rxjs/operators';
import {GroupComponent} from '../group-viewer/group.component';
import {ImageComponent} from '../image/image.component';
import {MarmosetComponent} from '../marmoset/marmoset.component';
import {VideoComponent} from '../video/video.component';

@Component({
    selector: 'frog-viewer',
    templateUrl: './viewer.component.html',
    styleUrls: ['./viewer.component.scss'],
    imports: [
        MatIcon,
        MatIconButton,
        MatTooltip,
        MatButton,
        MatMenuTrigger,
        ImageComponent,
        VideoComponent,
        MarmosetComponent,
        GroupComponent,
        MatMenu,
        MatMenuItem,
        AsyncPipe,
    ],
})
export class ViewerComponent extends ManagedComponent implements OnInit {
    @ViewChildren('assetViewer', { emitDistinctChangesOnly: true }) viewers: QueryList<IViewable>;

    public width: number;
    public height: number;
    public index: number;
    public assets: Asset[];
    public revision: number;
    public preferences$: Observable<any>;
    public revisions$: Observable<Asset[]>;
    public tools$: Observable<ViewerTool[]>;
    public AssetType = AssetType;
    public ColorSwatchMap = ColorSwatchMap;

    private streamId: string;
    private element;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private assetService: AssetService,
        private inspectorService: InspectorService,
        private stateService: StateService,
        private preferencesService: PreferencesService,
        private viewerToolsService: ViewerToolsService,
        private changeDetectionRef: ChangeDetectorRef,
    ) {
        super();

        this.width = window.innerWidth;
        this.height = window.innerHeight;
        this.index = 0;
        this.assets = [];
    }

    ngOnInit() {
        const viewingAssetObservable = this.stateService.state.pipe(
            map((state) => {
                this.streamId = state.streamId;
                return state.viewingAsset;
            }),
            filter((assetId) => assetId !== null),
            mergeMap((assetId) => this.assetService.resolve([assetId])),
            map((assets) => assets[0]),
            tap((asset) => {
                this.revisions$ = this.assetService.history(asset.id);
            }),
        );
        const assetsObservable = this.stateService.state.pipe(
            map((state) => state.selection),
            mergeMap((assetIds) => {
                if (assetIds && assetIds.length > 0) {
                    return this.assetService.resolve(assetIds);
                }

                return this.assetService.filteredAssets;
            }),
            mergeMap((assets) => {
                const assetIds = assets.map((asset) => asset.id);
                return this.assetService.resolve(assetIds);
            }),
        );
        let sub = combineLatest([viewingAssetObservable, assetsObservable]).subscribe(
            ([viewingAsset, viewableAssets]) => {
                this.index = -1;
                for (let i = 0; i < viewableAssets.length; i++) {
                    if (viewableAssets[i].id === viewingAsset.id) {
                        this.index = i;
                        break;
                    }
                }
                if (this.index === -1) {
                    // Not found in filteredAssets
                    viewableAssets.unshift(viewingAsset);
                    this.index = 0;
                }
                this.assets = viewableAssets;
                this.assetService.viewAsset(this.assets[this.index]);
            },
        );
        this.subscriptions.push(sub);

        sub = this.assetService.assetUpdated.subscribe((asset) => {
            for (let i = 0; i < this.assets.length; i++) {
                if (this.assets[i].id === asset.id) {
                    this.assets[i] = asset;
                    break;
                }
            }
        });
        this.subscriptions.push(sub);

        this.preferences$ = this.preferencesService.preferences;
        this.tools$ = this.viewerToolsService.tools$.pipe(
            // Hack to avoid ExpressionChangedAfterItHasBeenCheckedError
            delay(0),
            map((tools) => {
                const toolsCopy = tools.slice();
                toolsCopy.splice(
                    0,
                    0,
                    new ViewerTool({
                        icon: 'info',
                        tooltip: 'Open asset Info Panel',
                        action: () => {
                            this.setFocus(this.assets[this.index]);
                        },
                    }),
                );

                return toolsCopy;
            }),
        );
    }

    // @HostListener('window:stateChange', ['$event'])
    // stateChange() {
    //     // this.stopSlideShow();
    // }

    @HostListener('window:resize', ['$event'])
    resize(event) {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        this.fitToWindow();
    }

    @HostListener('window:keydown', ['$event'])
    keyDownEvent(event: KeyboardEvent) {
        const target = event.target as HTMLElement;
        if (
            target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable
        ) {
            // User is editing a form control
            return;
        }

        if (this.inspectorService.isInspecting) {
            return;
        }

        let triggered = false;
        if (event.key === 'Tab') {
            triggered = true;
            this.setFocus(this.assets[this.index]);
        }
        if (event.key === 'ArrowLeft' || event.key === 'Left' || event.key === 'a') {
            triggered = true;
            this.previous();
        }
        if (event.key === 'ArrowRight' || event.key === 'Right' || event.key === 'd') {
            triggered = true;
            this.next();
        }
        if (event.key === '2' || event.key === 'ArrowDown' || event.key === 's') {
            triggered = true;
            this.fitToWindow();
        }
        if (event.key === '1' || event.key === 'ArrowUp' || event.key === 'w') {
            triggered = true;
            this.original();
        }
        if (event.key === 'Escape' || event.key === 'Esc') {
            triggered = true;
            this.close(event);
        }

        if (triggered) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    next() {
        let index: number;

        index = this.index + 1;
        index = index > this.assets.length - 1 ? 0 : index;

        this.setIndex(index);
    }

    previous() {
        let index = this.index - 1;
        index = index < 0 ? this.assets.length - 1 : index;
        this.setIndex(index);
    }

    original() {
        this.viewers.forEach((viewer) => viewer.scaleToSource());
    }

    fitToWindow() {
        this.viewers.forEach((viewer) => viewer.scaleToWindow());
    }

    setFocus(asset: Asset) {
        this.inspectorService.inspect(asset);
    }

    close(event: Event) {
        if (event !== null) {
            event.preventDefault();
        }

        if (this.route.snapshot.params.streamId) {
            const navigationExtras: NavigationExtras = {
                queryParams: { q: decodeURIComponent(window.location.search.substr(3)) },
            };
            this.router.navigate(['/s', this.route.snapshot.params.streamId], navigationExtras);
        } else {
            this.router.navigate(['/']);
        }
    }

    setIndex(index: number) {
        this.index = index;
        if (this.assets.length > 0) {
            const navigationExtras: NavigationExtras = {
                queryParams: { q: decodeURIComponent(window.location.search.substr(3)) },
                replaceUrl: true,
            };
            const asset = this.assets[this.index];
            const url = ['/v', this.streamId, asset.id];
            this.router.navigate(url, navigationExtras);
        }
    }

    setRevision(revision: Asset) {
        this.revision = revision.revision + 1;
        this.assets[this.index] = Object.assign(this.assets[this.index], revision);
    }
}
